import { supabase } from '../../lib/supabaseClient';
import type { Database } from '../../types/database.types';
import { buildEml, sanitizeEmlFilename, toBase64Lines, type EmlAttachment } from './emailEml';
import { getDownloadUrl } from '../files/filesService';

export type DossierEmailRow = Database['public']['Tables']['dossier_emails']['Row'] & {
  /** Présent en prod (add-in Lot 2). Absent des types main tant que Lot 2 n’est pas mergé. */
  ms_item_id?: string | null;
};
export type GmailRow = Database['public']['Tables']['gmail_archived_emails']['Row'];
export type MergedEmail =
  | { kind: 'gmail'; sentAt: string; row: GmailRow }
  | { kind: 'graft'; sentAt: string; row: DossierEmailRow };

export async function listDossierGraftEmails(dossierId: string): Promise<DossierEmailRow[]> {
  const { data, error } = await supabase.from('dossier_emails')
    .select('*').eq('dossier_id', dossierId).eq('status', 'grafted')
    .order('sent_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function listMyPendingEmails(): Promise<DossierEmailRow[]> {
  const { data, error } = await supabase.from('dossier_emails')
    .select('*').eq('status', 'pending_triage').order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? []; // la RLS scope déjà : soi-même, ou tout si admin
}

async function invokeAction(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Session expirée');
  const { data, error } = await supabase.functions.invoke('dossier-email-action', {
    body, headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw new Error("L'action a échoué.");
  if (data && (data as { error?: string }).error) throw new Error(String((data as { error: string }).error));
  return data as Record<string, unknown>;
}
export const graftPendingEmail = (emailId: string, dossierId: string) =>
  invokeAction({ action: 'graft', email_id: emailId, dossier_id: dossierId });
export const removeGraftedEmail = (emailId: string) =>
  invokeAction({ action: 'remove', email_id: emailId });
export const discardPendingEmail = (emailId: string) =>
  invokeAction({ action: 'discard', email_id: emailId });
export const moveGraftedEmail = (emailId: string, targetDossierId: string) =>
  invokeAction({ action: 'move', email_id: emailId, dossier_id: targetDossierId });

async function fetchGraftAttachments(dossierId: string, emailitId: string): Promise<EmlAttachment[]> {
  const { data, error } = await supabase.from('dossier_files')
    .select('id, filename, mime')
    .eq('dossier_id', dossierId).eq('source', 'email')
    .like('source_ref', `${emailitId}::%`);
  if (error) throw error;
  return Promise.all((data ?? []).map(async (f) => {
    const url = await getDownloadUrl(f.id);
    const buf = await (await fetch(url)).arrayBuffer();
    return {
      filename: f.filename,
      contentType: f.mime,
      base64: toBase64Lines(new Uint8Array(buf)).replace(/\r\n/g, ''),
    };
  }));
}

/** Génère le .eml du mail (PJ re-embarquées pour les mails greffés) et le télécharge. */
export async function downloadEmailAsEml(email: MergedEmail, dossierId: string): Promise<void> {
  const attachments = email.kind === 'graft' && email.row.emailit_id
    ? await fetchGraftAttachments(dossierId, email.row.emailit_id)
    : []; // mails Gmail : pas de PJ re-embarquées (hors périmètre lot 4)
  const eml = buildEml({
    from: email.row.from_email ?? '',
    to: email.kind === 'graft' ? email.row.to_emails : [],
    cc: email.kind === 'graft' ? email.row.cc_emails : [],
    subject: email.row.subject,
    sentAt: email.sentAt,
    bodyHtml: email.row.body_html,
    bodyText: email.row.body_text,
    attachments,
  });
  const blob = new Blob([eml], { type: 'message/rfc822' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = sanitizeEmlFilename(email.row.subject);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
